// replay-browse.ts — the recording browser shared by the replay viewers: a transport-bar button
// that opens an overlay listing the R2 archive (GET /api/replays — the endpoint aggregates all
// R2 pages server-side and currently always returns cursor:null; the client still follows
// `cursor` for forward-compat), newest first. Picking a recording runs `opts.onPick(key)` — the
// standalone page's default reloads with ?replay=<key> (served same-origin by functions/r/); the
// embedded game viewer swaps recordings in place. `opts.apiBase` points the archive fetch at
// another origin (the game client → replay.warfareincorporated.com, which serves CORS *).
// Self-contained DOM (no page-layout coupling beyond the button), closes on Esc / click-out, and
// degrades to a message when the archive API is absent (e.g. a local static server with no Pages
// Functions).
import { escapeHtml } from './replay-common.ts';

type GameDetail = {
  map: string;
  endUtc: number;
  durationSecs: number;
  players: { name: string; winner: boolean; ai: boolean }[];
};
type Entry = { key: string; size: number; uploaded: string; game?: GameDetail };

const MAX_ENTRIES = 3000; // ~6 R2 pages; plenty until a D1 index takes over

// "Quiet" recordings — tests / no-action games. The .wirep grows with issued commands, so size
// is a direct action proxy: the archive is ~80% sub-50KB test recordings while every real game
// runs 100KB+. Hidden by default; the header link toggles them (persisted).
const QUIET_BYTES = 50 * 1024;
const SHOW_ALL_KEY = 'wiReplayBrowseAll';

// The list PRELOADS at viewer boot (initReplayBrowse kicks the fetch) and snapshots into
// sessionStorage, so the panel opens instantly: cached entries render immediately while a
// background refresh replaces them. The archive only grows ~once a minute; staleness is cheap.
const CACHE_KEY = 'wiReplayBrowseList';
let preloaded: Promise<Entry[]> | null = null;
function cachedEntries(): Entry[] | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw !== null ? (JSON.parse(raw) as Entry[]) : null;
  } catch {
    return null;
  }
}
function preload(apiBase: string): Promise<Entry[]> {
  preloaded ??= fetchAll(apiBase).then((entries) => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(entries));
    } catch {
      /* quota/private mode - the in-memory promise still serves this page */
    }
    return entries;
  });
  return preloaded;
}

async function fetchAll(apiBase: string): Promise<Entry[]> {
  const out: Entry[] = [];
  let cursor: string | null = null;
  do {
    const r: Response = await fetch(
      `${apiBase}/api/replays${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    );
    if (!r.ok) throw new Error(`archive ${r.status}`);
    const page = (await r.json()) as { replays: Entry[]; cursor: string | null };
    out.push(...page.replays);
    cursor = page.cursor;
  } while (cursor !== null && out.length < MAX_ENTRIES);
  // keys are <yyyy-mm-dd>/<gameId>.wirep — lexicographic descending = newest first (numeric ids
  // within a day compared by length first so 10.wirep sorts after 9.wirep)
  return out.sort((a, b) => {
    const [da, ia] = a.key.split('/'),
      [db, ib] = b.key.split('/');
    if (da !== db) return da < db ? 1 : -1;
    return ib.length - ia.length || (ia < ib ? 1 : -1);
  });
}

const kb = (n: number): string =>
  n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} kB`;
const esc = escapeHtml;
const when = (unixSecs: number): string => {
  const d = new Date(unixSecs * 1000);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
// one panel row: rich two-line entry when the archive API joined a game row (map, roster with
// winners bold, duration — the same detail the dashboard's played-games table shows), else the key
function rowHtml(e: Entry, current: string | null): string {
  const on = e.key === current;
  const right: string[] = [];
  let top = `<span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(e.key)}</span>`;
  let bottom = '';
  if (e.game) {
    const g = e.game;
    const players =
      g.players
        .filter((p) => !p.ai) // overmind seat-fillers aren't playing (dashboard does the same)
        .map((p) => (p.winner ? `<b style="color:#cfc">${esc(p.name)} 🏆</b>` : esc(p.name)))
        .join(', ') || '<span style="color:#6a6">—</span>';
    const len = g.durationSecs >= 60 ? `${Math.round(g.durationSecs / 60)}m` : `${g.durationSecs}s`;
    right.push(len);
    top = `<span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap"><b>${esc(g.map)}</b> · ${players}</span>`;
    bottom = `<div style="display:flex;gap:12px;color:#6a6;font-size:11px"><span style="flex:1">${when(g.endUtc)}</span><span>${esc(e.key)}</span></div>`;
  }
  right.push(kb(e.size));
  return (
    `<div data-key="${esc(e.key)}" style="padding:4px 8px;cursor:pointer;border-radius:3px;${on ? 'background:#1a4d2e;color:#cfc' : ''}" onmouseover="this.style.background='#123'" onmouseout="this.style.background='${on ? '#1a4d2e' : ''}'">` +
    `<div style="display:flex;gap:12px">${top}<span style="color:#6a6;white-space:nowrap">${right.join(' · ')}</span></div>${bottom}</div>`
  );
}

export interface ReplayBrowseOpts {
  /** the /api/replays origin ('' = same-origin — the standalone viewer page). */
  apiBase?: string;
  /** the currently-playing archive key (highlighted; always shown through the quiet filter). */
  current?: string | null;
  /** picking a recording (default: reload the page with ?replay=<key>). */
  onPick?: (key: string) => void;
}

export function initReplayBrowse(btn: HTMLElement | null, opts: ReplayBrowseOpts = {}): void {
  if (btn === null) return;
  const apiBase = opts.apiBase ?? '';
  const pick =
    opts.onPick ??
    ((key: string): void => {
      const q = new URLSearchParams(location.search);
      q.delete('wirep');
      q.set('replay', key);
      location.search = q.toString(); // reload — the boot path picks up ?replay=
    });
  preload(apiBase).catch(() => {}); // warm the archive list while the viewer boots
  btn.addEventListener('click', () => {
    const open = document.getElementById('browsepanel') as
      (HTMLElement & { closeBrowse?: () => void }) | null;
    if (open) {
      open.closeBrowse?.(); // unhooks the modal's key listener too
      return;
    }
    const wrap = document.createElement('div') as HTMLDivElement & { closeBrowse?: () => void };
    wrap.id = 'browsepanel';
    wrap.style.cssText =
      'position:fixed;inset:0;z-index:40;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.72)';
    const panel = document.createElement('div');
    panel.style.cssText =
      'background:#0d1410;border:1px solid #0a0;border-radius:6px;padding:12px 0 8px;width:min(640px,94vw);max-height:min(560px,82vh);display:flex;flex-direction:column;font:12px ui-monospace,monospace;color:#8f8';
    panel.innerHTML =
      '<div style="padding:0 16px 8px;color:#8f8;font-size:14px">Recordings <span id="browsecount" style="color:#6a6;font-size:11px"></span></div>' +
      '<div id="browselist" style="overflow-y:auto;flex:1 1 auto;padding:0 8px">loading…</div>';
    panel.onclick = (e) => e.stopPropagation();
    wrap.appendChild(panel);
    // one close() for every dismissal path (Esc, click-out, ☰ re-toggle) so the key listener
    // never leaks; capture phase + stopPropagation = the modal owns the keyboard while open
    // (otherwise Space/r/arrows keep driving the viewer's playback behind it)
    const onKey = (e: KeyboardEvent): void => {
      if (!wrap.isConnected) {
        removeEventListener('keydown', onKey, true); // self-heal if removed some other way
        return;
      }
      e.stopPropagation();
      if (e.key === 'Escape') close();
    };
    const close = (): void => {
      wrap.remove();
      removeEventListener('keydown', onKey, true);
    };
    wrap.closeBrowse = close;
    wrap.onclick = close;
    addEventListener('keydown', onKey, true);
    document.body.appendChild(wrap);

    const list = panel.querySelector('#browselist') as HTMLElement;
    // delegated once at panel creation, so rows rendered from the sessionStorage cache are
    // clickable immediately (not only after the background refresh resolves)
    list.addEventListener('click', (e) => {
      const row = (e.target as HTMLElement).closest('[data-key]') as HTMLElement | null;
      if (row === null) return;
      close(); // unhooks the modal's key listener before handing off (an in-place pick doesn't navigate)
      pick(row.getAttribute('data-key')!);
    });
    const current =
      opts.current !== undefined
        ? opts.current
        : new URLSearchParams(location.search).get('replay');
    let showAll = localStorage.getItem(SHOW_ALL_KEY) === 'on';
    let last: Entry[] = [];
    const render = (entries: Entry[]): void => {
      last = entries;
      // the currently-playing recording always shows, quiet or not
      const vis = showAll
        ? entries
        : entries.filter((e) => e.size >= QUIET_BYTES || e.key === current);
      const hidden = entries.length - vis.length;
      const count = panel.querySelector('#browsecount') as HTMLElement;
      count.innerHTML =
        `\u00b7 ${vis.length}` +
        (hidden > 0 || showAll
          ? ` <a id="browseall" style="color:#5c8;cursor:pointer;text-decoration:underline">${
              showAll ? 'hide quiet recordings' : `${hidden} quiet hidden \u2014 show all`
            }</a>`
          : '');
      count.querySelector('#browseall')?.addEventListener('click', () => {
        showAll = !showAll;
        localStorage.setItem(SHOW_ALL_KEY, showAll ? 'on' : 'off');
        render(last);
      });
      if (vis.length) list.innerHTML = vis.map((e) => rowHtml(e, current)).join('');
      else list.textContent = 'archive is empty';
    };
    const cached = cachedEntries();
    if (cached !== null && cached.length > 0) render(cached); // instant open; refresh lands below
    preload(apiBase)
      .then(render)
      .catch((err: unknown) => {
        preloaded = null; // a failed fetch shouldn't poison future opens
        if (cached === null || cached.length === 0)
          list.textContent = `archive unavailable (${err instanceof Error ? err.message : String(err)})`;
      });
  });
}
